import CuppingAnimation from '@/pages/home/components/CuppingAnimation';
import { BOOKING_URL } from '@/constants';

const types = [
  { icon: 'ri-fire-line', label: 'Ventouses à feu', desc: 'Aspiration puissante, douleurs ciblées' },
  { icon: 'ri-drop-line', label: 'Ventouses sèches', desc: 'Succion par aspiration' },
  { icon: 'ri-hand-heart-line', label: 'Ventouses glissantes', desc: 'Massage des tissus' },
];

const steps = [
  {
    num: '01',
    title: 'Consultation initiale (gratuite)',
    desc: 'On évalue vos besoins, antécédents et objectifs pour définir le protocole.',
  },
  {
    num: '02',
    title: 'Préparation',
    desc: 'Nettoyage de la zone et installation confortable.',
  },
  {
    num: '03',
    title: 'Pose des ventouses',
    desc: 'Verre, bambou ou flamme selon le soin — points ciblés.',
  },
  {
    num: '04',
    title: 'Séance 30 à 60 min',
    desc: 'Le soin agit ; vous repartez avec des conseils de récupération.',
  },
];

const expect = [
  {
    icon: 'ri-time-line',
    title: 'Durée',
    desc: '30 à 60 minutes selon le protocole, consultation incluse.',
  },
  {
    icon: 'ri-information-line',
    title: 'Marques passagères',
    desc: 'Des ronds colorés peuvent apparaître et s’estompent en quelques jours — c’est normal.',
  },
  {
    icon: 'ri-drop-line',
    title: 'Succion à sec',
    desc: 'Les ventouses agissent par aspiration sur la peau, en mode sec.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Hygiène',
    desc: 'Matériel soigné et protocole d’hygiène strict — point souvent souligné par nos patients.',
  },
];

export default function CuppingSection() {
  return (
    <section id="cupping" className="pb-16 px-6 scroll-mt-[84px] bg-[#f6f3ec]">
      <div className="max-w-[1180px] mx-auto">
        <div className="max-w-[680px] mx-auto mb-10 text-center">
          <span className="text-[#0b6b52] text-xs font-semibold tracking-widest uppercase mb-3 block">
            Nos Soins
          </span>
          <h2
            className="text-[clamp(34px,4.4vw,52px)] font-bold leading-[1.12] text-[#173029] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Cupping Thérapie
          </h2>
          <p className="text-base leading-relaxed text-[#57534a] text-pretty">
            Une technique de ventouses pour stimuler la circulation, soulager les tensions et favoriser la
            récupération — expliquée simplement, sans jargon inutile.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-12">
          <div className="relative">
            <CuppingAnimation />
          </div>

          <div>
            <h3
              className="text-2xl font-bold text-[#173029] mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Comment se déroule une séance ?
            </h3>
            <p className="text-[#57534a] leading-relaxed mb-8">
              Le cupping crée une succion sur la peau pour travailler les tissus en profondeur. Idéal pour
              douleurs musculaires, stress, fatigue ou récupération sportive.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
              {types.map((t) => (
                <div
                  key={t.label}
                  className="bg-white border border-[#e8e2d4] rounded-[14px] p-4 flex items-center gap-3"
                >
                  <i className={`${t.icon} text-[#0b6b52] text-xl flex-shrink-0`}></i>
                  <div>
                    <div className="font-semibold text-[13.5px] text-[#22201c]">{t.label}</div>
                    <div className="text-xs text-[#8a8578]">{t.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-[11px] font-semibold tracking-[0.26em] uppercase text-[#8a8578] mb-5">
              Déroulement
            </div>
            <div className="flex flex-col">
              {steps.map((s, i) => (
                <div
                  key={s.num}
                  className={`flex gap-5 py-4 border-t border-[#e8e2d4] ${
                    i === steps.length - 1 ? 'border-b' : ''
                  }`}
                >
                  <span
                    className="text-[22px] text-[#0b6b52] w-10 flex-shrink-0"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {s.num}
                  </span>
                  <div>
                    <div className="font-semibold text-[15px] text-[#22201c]">{s.title}</div>
                    <div className="text-[13.5px] leading-relaxed text-[#8a8578] mt-0.5">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Onboard: à quoi s'attendre */}
        <div id="a-quoi-s-attendre" className="scroll-mt-[84px]">
          <div className="max-w-[640px] mx-auto mb-10 text-center">
            <span className="text-[#0b6b52] text-xs font-semibold tracking-widest uppercase mb-3 block">
              Première visite
            </span>
            <h3
              className="text-2xl md:text-3xl font-bold text-[#173029] mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              À quoi s&apos;attendre
            </h3>
            <p className="text-sm text-[#57534a] leading-relaxed">
              Les questions les plus fréquentes avant une première séance — pour venir serein.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {expect.map((e) => (
              <div
                key={e.title}
                className="bg-white border border-[#e8e2d4] rounded-2xl p-5"
              >
                <i className={`${e.icon} text-[#0b6b52] text-xl mb-3 block`}></i>
                <div className="font-semibold text-sm text-[#22201c] mb-1.5">{e.title}</div>
                <p className="text-[13px] leading-relaxed text-[#8a8578] m-0">{e.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <a
              href={BOOKING_URL}
              className="inline-flex items-center gap-2 text-[#0b6b52] font-semibold text-sm hover:text-[#08553f] transition-colors"
            >
              Une question avant de venir ? Demandez un créneau
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
